import { Head, Link } from "@inertiajs/react"

import { formatDate } from "@/components/products/work_orders/status-chip"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface HeadlineMetric {
  metric_key: string
  label: string
  value: number | null
  unit: string | null
  missing_data: boolean
}

interface SubjectRow {
  id: number
  type: "Lessor" | "MruTeam"
  name: string
  scorecard_id: number | null
  period: string | null
  generated_at: string | null
  headline: HeadlineMetric[]
}

interface Props {
  lessors: SubjectRow[]
  mru_teams: SubjectRow[]
  periods: string[]
}

/**
 * Who we work with, and how they're doing — the categories of the deck the
 * customer already circulates internally, so the numbers land in a meeting
 * without translation.
 */
export default function ScorecardsIndex({
  lessors,
  mru_teams,
  periods,
}: Props) {
  const routes = useOrgRoutes()

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Scorecards", href: routes.scorecardsPath() },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Scorecards" />

      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Scorecards</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Quarterly performance for every lessor and MRU team
            {periods.length > 0 &&
              ` — ${periods.length} period${periods.length === 1 ? "" : "s"} on record`}
            .
          </p>
        </div>

        <Section title="Lessors" rows={lessors} />
        <Section title="MRU teams" rows={mru_teams} />
      </div>
    </AppLayout>
  )
}

function Section({ title, rows }: { title: string; rows: SubjectRow[] }) {
  if (rows.length === 0) return null

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((row) => (
          <SubjectCard key={`${row.type}-${row.id}`} row={row} />
        ))}
      </div>
    </section>
  )
}

function SubjectCard({ row }: { row: SubjectRow }) {
  const routes = useOrgRoutes()

  const body = (
    <Card
      className={
        row.scorecard_id
          ? "hover:border-primary/50 h-full transition-colors"
          : "h-full"
      }
    >
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <CardTitle className="text-base">{row.name}</CardTitle>
        {row.period && <Badge variant="outline">{row.period}</Badge>}
      </CardHeader>
      <CardContent>
        {row.scorecard_id ? (
          <>
            <dl className="space-y-1.5">
              {row.headline.map((metric) => (
                <div
                  key={metric.metric_key}
                  className="flex items-baseline justify-between gap-3"
                >
                  <dt className="text-muted-foreground text-sm">
                    {metric.label}
                  </dt>
                  <dd className="text-sm font-semibold tabular-nums">
                    {/* NO DATA IS NOT ZERO — a quarter with nothing to grade
                        must never render as a perfect score. */}
                    {metric.missing_data
                      ? "No data"
                      : `${metric.value}${metric.unit === "percent" ? "%" : metric.unit === "days" ? "d" : ""}`}
                  </dd>
                </div>
              ))}
            </dl>
            {row.generated_at && (
              <p className="text-muted-foreground mt-3 text-xs">
                Generated {formatDate(row.generated_at)}
              </p>
            )}
          </>
        ) : (
          <p className="text-muted-foreground text-sm">No scorecard yet.</p>
        )}
      </CardContent>
    </Card>
  )

  if (!row.scorecard_id) return body

  return <Link href={routes.scorecardPath(row.scorecard_id)}>{body}</Link>
}
